if (!customElements.get('sticky-purchase-bar')) {
  class StickyPurchaseBar extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      if (this.closest('quick-add-modal')) {
        this.remove();
        return;
      }

      this.initialized = true;
      this.sectionId = this.dataset.sectionId;
      this.stickyButton = this.querySelector('[data-sticky-submit]');
      this.stickyLabel = this.querySelector('[data-sticky-submit-label]');
      this.stickyPrice = this.querySelector('[data-sticky-price]');
      this.stickyVariant = this.querySelector('[data-sticky-variant]');
      this.stickyImage = this.querySelector('[data-sticky-image]');
      this.footerVisible = false;
      this.purchaseControlsPassed = false;

      this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!this.mainButton || !this.stickyButton) {
        this.remove();
        return;
      }

      this.purchaseControls = this.mainButton.closest('.product-buy-buttons-block') || this.mainButton;
      this.handleStickyClick = this.handleStickyClick.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);
      this.handleViewportChange = this.handleViewportChange.bind(this);
      this.stickyButton.addEventListener('click', this.handleStickyClick);
      window.addEventListener('scroll', this.handleViewportChange, { passive: true });
      window.addEventListener('resize', this.handleViewportChange, { passive: true });
      window.addEventListener('pageshow', this.handleViewportChange);

      this.purchaseObserver = new IntersectionObserver(
        () => this.handleViewportChange(),
        { threshold: 0 }
      );
      this.purchaseObserver.observe(this.purchaseControls);

      const footer = document.querySelector('.shopify-section-group-footer-group, footer, .ms-footer');
      this.footer = footer;
      if (footer) {
        this.footerObserver = new IntersectionObserver(
          () => this.handleViewportChange(),
          { threshold: 0 }
        );
        this.footerObserver.observe(footer);
      }

      this.buttonObserver = new MutationObserver(() => this.syncButton());
      this.buttonObserver.observe(this.mainButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled'],
        childList: true,
        characterData: true,
        subtree: true,
      });

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
      }

      this.syncFromPage();
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.stickyButton?.removeEventListener('click', this.handleStickyClick);
      this.purchaseObserver?.disconnect();
      this.footerObserver?.disconnect();
      this.buttonObserver?.disconnect();
      window.removeEventListener('scroll', this.handleViewportChange);
      window.removeEventListener('resize', this.handleViewportChange);
      window.removeEventListener('pageshow', this.handleViewportChange);
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = 0;
      if (typeof this.unsubscribeVariant === 'function') this.unsubscribeVariant();
      this.initialized = false;
    }

    handleViewportChange() {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = 0;
        if (!this.isConnected || !this.purchaseControls?.isConnected) return;
        const controls = this.purchaseControls.getBoundingClientRect();
        this.purchaseControlsPassed = controls.height > 0 && controls.bottom < 0;
        const footer = this.footer?.getBoundingClientRect();
        this.footerVisible = Boolean(footer && footer.height > 0 && footer.top < window.innerHeight && footer.bottom > 0);
        this.updateVisibility();
      });
    }

    handleStickyClick() {
      const mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
      if (!mainButton || mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true') return;
      mainButton.click();
    }

    handleVariantChange({ data } = {}) {
      if (!data || data.sectionId !== this.sectionId) return;
      requestAnimationFrame(() => this.syncFromPage(data.variant));
    }

    syncFromPage(variant) {
      this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`) || this.mainButton;
      this.syncButton();

      const currentPrice = document.getElementById(`price-${this.sectionId}`);
      if (currentPrice && this.stickyPrice) this.stickyPrice.innerHTML = currentPrice.innerHTML;

      if (!variant) return;

      if (this.stickyVariant) {
        const options = (variant.options || []).filter((option) => option && option !== 'Default Title');
        this.stickyVariant.textContent = options.join(' · ');
        this.stickyVariant.hidden = options.length === 0;
      }

      const previewImage = variant.featured_media?.preview_image;
      const imageSource = previewImage?.src || previewImage?.url;
      if (imageSource && this.stickyImage) {
        this.stickyImage.src = imageSource;
        this.stickyImage.removeAttribute('srcset');
      }
    }

    syncButton() {
      if (!this.mainButton || !this.stickyButton) return;
      const mainLabel = this.mainButton.querySelector('span')?.textContent?.trim();
      if (mainLabel && this.stickyLabel) this.stickyLabel.textContent = mainLabel;

      const disabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
      this.stickyButton.disabled = disabled;
      this.stickyButton.setAttribute('aria-disabled', String(disabled));
    }

    updateVisibility() {
      const visible = this.purchaseControlsPassed && !this.footerVisible;
      if (this.classList.contains('is-visible') === visible && this.getAttribute('aria-hidden') === String(!visible)) return;
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      if (this.stickyButton) this.stickyButton.tabIndex = visible ? 0 : -1;
    }
  }

  customElements.define('sticky-purchase-bar', StickyPurchaseBar);
}


